import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { Zap, Loader } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BillingPage = () => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [isPro, setIsPro] = useState(false);
  const [cancelled, setCancelled] =
    useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] =
  useState(false);
  const [expiresAt, setExpiresAt] =
    useState<string | null>(null);
  const [cancelling, setCancelling] =
    useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const token = await
        getAccessTokenSilently({
          authorizationParams: {
            audience:
              "https://api.scaffoldbridge.io"
          }
        });

      const response = await axios.get(
         `${process.env.REACT_APP_API_URL
            || 'http://localhost:5062'
          }/api/billing/status`,
        { headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setIsPro(response.data.isPro);
      setCancelAtPeriodEnd(response.data.cancelAtPeriodEnd);
      setExpiresAt(response.data.expiresAt);
    } catch {}
  };

  const handleCancel = async () => {
    if (!window.confirm(
      'Cancel your Pro subscription? ' +
      'You keep access until end of ' +
      'billing period.'))
      return;

    setCancelling(true);
    try {
      const token = await
        getAccessTokenSilently({
          authorizationParams: {
            audience:
              "https://api.scaffoldbridge.io"
          }
        });

      await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5062'}/api/billing/cancel`,
        {},
        { headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setCancelled(true);
      toast.success(
        'Subscription cancelled. ' +
        'Access continues until ' +
        'end of billing period.');

    } catch (err: any) {
      toast.error(
        err.response?.data?.error ||
        'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const handleUpgrade = async () => {
    try {
      const token = await
        getAccessTokenSilently({
          authorizationParams: {
            audience:
              "https://api.scaffoldbridge.io"
          }
        });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5062'}/api/billing/checkout`,
        {},
        { headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Redirect to Stripe checkout ✅
      window.location.href =
        response.data.url;

    } catch (err) {
      toast.error('Failed to start checkout');
    }
  };

    const handleReactivate = async () => {
    try {
        const token = await
        getAccessTokenSilently({
            authorizationParams: {
            audience:
                "https://api.scaffoldbridge.io"
            }
        });

        await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5062'}/api/billing/reactivate`,
        {},
        { headers: {
            Authorization: `Bearer ${token}`
            }
        }
        );

        toast.success(
        'Subscription reactivated! ✅');

        // Reload status ✅
        await loadStatus();

    } catch (err: any) {
        toast.error(
        err.response?.data?.error ||
        'Failed to reactivate');
    }
    };

  return (
    <div className="min-h-screen
                    bg-gray-950 py-16 px-4">
      <div className="max-w-2xl mx-auto">

        <h1 className="text-3xl font-bold
                       text-blue-200 mb-8">
          Billing & Subscription
        </h1>

        {/* ⚠️ Cancelled Warning ✅ */}
        {isPro && cancelAtPeriodEnd && (
          <div className="bg-yellow-500/10
                          border
                          border-yellow-500/20
                          rounded-2xl p-5 mb-6">
            <p className="text-yellow-400
                          font-semibold mb-1">
              ⚠️ Subscription Cancelled
            </p>
            <p className="text-gray-400
                          text-sm">
              Your Pro access continues until{' '}
              <span className="text-white
                               font-semibold">
                {expiresAt
                  ? new Date(expiresAt)
                      .toLocaleDateString(
                        'en-US', {
                          year:  'numeric',
                          month: 'long',
                          day:   'numeric'
                        })
                  : 'end of billing period'
                }
              </span>
              . After that you will be
              moved to the free plan.
            </p>
            {/* Resubscribe button ✅ */}
                <button
                onClick={handleReactivate}
                className="mt-3 text-blue-400
                            hover:text-blue-300
                            text-sm font-semibold
                            transition-colors">
                Resubscribe →
                </button>
          </div>
        )}

        {/* Current Plan ✅ */}
        <div className="bg-gray-900 border
                        border-gray-800
                        rounded-2xl p-6 mb-6">
          <h2 className="text-blue-200
                         font-semibold mb-4">
            Current Plan
          </h2>

          <div className="flex items-center
                          justify-between">
            <div>
              <p className="text-white
                            font-semibold
                            text-lg">
                {isPro
                  ? 'Pro Plan ⚡'
                  : 'Free Plan'}
              </p>
              <p className="text-gray-500
                            text-sm">
                {isPro
                  ? cancelAtPeriodEnd
                    ? `Cancels on ${expiresAt
                        ? new Date(expiresAt)
                            .toLocaleDateString()
                        : '...'}`
                    : '$49/month — Unlimited'
                  : '5 generations/month'
                }
              </p>
            </div>

            <span className={`px-3 py-1.5
                              rounded-full
                              text-sm font-mono ${
              isPro
                ? cancelAtPeriodEnd
                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'bg-gray-700 text-gray-400'
            }`}>
              {isPro
                ? cancelAtPeriodEnd
                  ? 'Cancels ⚠️'
                  : 'Active ✅'
                : 'Free'
              }
            </span>
          </div>
        </div>

        {/* Pro Features ✅ */}
        {isPro && (
          <div className="bg-gray-900 border
                          border-gray-800
                          rounded-2xl p-6 mb-6">
            <h2 className="text-blue-200
                           font-semibold mb-4">
              Your Pro Features
            </h2>
            <ul className="space-y-2">
              {[
                'Unlimited generations',
                'GitHub push delivery',
                'Docker image generation',
                'CI/CD pipeline',
                'Priority support'
              ].map(f => (
                <li key={f}
                    className="flex items-center
                               gap-2 text-gray-400
                               text-sm">
                  <span className="text-green-400">
                    ✓
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Upgrade if free ✅ */}
        {!isPro && (
          <div className="bg-gray-900 border
                          border-blue-500/20
                          rounded-2xl p-6 mb-6">
            <h2 className="text-blue-200
                           font-semibold mb-2">
              Upgrade to Pro
            </h2>
            <p className="text-gray-500
                          text-sm mb-4">
              Unlimited generations + GitHub
              push + Docker + CI/CD for
              $49/month
            </p>
            <button
              onClick={handleUpgrade}
              className="bg-blue-600
                         hover:bg-blue-500
                         text-white font-semibold
                         px-6 py-3 rounded-xl
                         transition-colors
                         flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Upgrade to Pro — $49/month
            </button>
          </div>
        )}

        {/* Cancel — only show if
            pro AND not already cancelled ✅ */}
        {isPro && !cancelAtPeriodEnd && (
          <div className="bg-gray-900 border
                          border-red-500/20
                          rounded-2xl p-6">
            <h2 className="text-red-400
                           font-semibold mb-2">
              Cancel Subscription
            </h2>
            <p className="text-gray-500
                          text-sm mb-4">
              Your Pro access continues until
              the end of your billing period.
              No refunds for partial months.
            </p>
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="bg-red-500/10
                         hover:bg-red-500/20
                         text-red-400
                         border border-red-500/30
                         font-medium px-6 py-3
                         rounded-xl
                         transition-colors
                         disabled:opacity-50
                         flex items-center gap-2">
              {cancelling ? (
                <>
                  <Loader className="w-4 h-4
                                     animate-spin"/>
                  Cancelling...
                </>
              ) : (
                'Cancel Subscription'
              )}
            </button>
          </div>
        )}

      </div>
    </div>
  );
};
export default BillingPage;